
import Vue from 'vue';
import _trim from 'lodash/trim';
import _debounce from 'lodash/debounce';
import { dataTableItemsOrderByPagination } from '@/utils/dataTables';
import { db, functions } from '@/firebase';
import { mapGetters, mapState, mapActions } from 'vuex';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { Team, CompanyCustomFieldKey, CompanyCustomFieldDef } from '@run-diffusion/shared';
import {
	DEFAULT_TEXT_MAX_LENGTH,
	getShowTextCounterAt,
	getTextMaxLengthRule,
} from '@/utils';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import DialogContent from '@/components/base/DialogContent.vue';
import ActionsIsland from '@/components/ActionsIsland.vue';
import SetTeamAllocationSettings from './SetTeamAllocationSettings/SetTeamAllocationSettings.vue';
import { TeamUserMixin } from '@/mixins';
import pluralize from 'pluralize';
import { updateTeamTokenAllocationSettings } from '@/views/Team/TeamDashboard/SetTeamAllocationSettings/utils';
import IsBoundToBudgetBadge from '@/views/Team/components/IsBoundToBudgetBadge.vue';

export default Vue.extend({
	name: 'EditTeams',
	props: {
		value: { type: Boolean, default: false },
		currTeams: { type: Array, default: () => [] },
		pagination: { type: Object, default: null },
	},
	mixins: [
		TeamUserMixin,
	],
	data () {
		const SHORT_TEXT_LENGTH: number = 100;
		return {
			// FORM
			formValid: true,
			DEFAULT_TEXT_MAX_LENGTH,
			SHORT_TEXT_LENGTH,
			rules: {
				name: [
					getTextMaxLengthRule(SHORT_TEXT_LENGTH),
					v => !!_trim(v) || 'Required',
				],
				customField: [
					getTextMaxLengthRule(),
				],
			},

			sortedTeams: [] as Team[],

			teamEditData: {},
			saving: false,
			expandedPanels: null,

			debouncedGlobalUpdate: () => {},
			isGlobalEdit: false,
			globalEditData: {
				monthlyRunnitTokensBudget: 0,
				isBoundToBudget: false,
			},
		};
	},
	created () {
		this.debouncedGlobalUpdate = _debounce(this.applyGlobalEditSettings, 250);
	},
	computed: {
		...mapState([
			'user',
		]),
		...mapGetters([
			'displayLabels',
		]),
		teamCustomFieldDefs(): CompanyCustomFieldDef[] {
			return this.team?.company?.teamCustomFieldDefs || [];
		},
		newSumMonthlyRunnitTokensBudget() {
			const currentTokens: number = this.team?.company?.sumMonthlyRunnitTokensBudget || 0;
			let oldBudgets: number = 0;
			let newBudgets: number = 0;
			this.sortedTeams.forEach((t: Team) => {
				oldBudgets += t.allotmentSettings?.monthlyRunnitTokensBudget > 0 ? t.allotmentSettings.monthlyRunnitTokensBudget : 0;
				newBudgets += this.getTeamEditData(t.id).monthlyRunnitTokensBudget > 0 ? this.getTeamEditData(t.id).monthlyRunnitTokensBudget : 0;
			});
			return currentTokens - oldBudgets + newBudgets;
		},
	},
	watch: {
		value: {
			immediate: true,
			handler (newVal: boolean, oldVal: boolean) {
				if (newVal && !oldVal) {
					this.$nextTick(() => {
						// Set initial expanded state
						this.expandedPanels = this.currTeams.length === 1 ? 0 : null;
					});
				}
				if (newVal && newVal !== oldVal) {
					this.initTeamEditData(this.sortedTeams);
				}
			},
		},
		sortedTeams: {
			immediate: true,
			handler (newVal: Team[], oldVal: Team[]) {
				if (newVal !== oldVal) {
					this.initTeamEditData(newVal);
				}
			},
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		getShowTextCounterAt,
		setOpen (val: boolean) {
			this.$emit('input', val);
			if (val) {
				this.sortedTeams = dataTableItemsOrderByPagination(this.currTeams, this.pagination);
			} else {
				this.isGlobalEdit = false;
				this.clearGlobalEditSettings();
			}
		},
		onClose () {
			this.setOpen(false);
		},
		getTeamEditData (teamId: string) {
			const defaultData = {
				name: null,
				customFields: {},
				monthlyRunnitTokensBudget: 0,
				isBoundToBudget: false,
			};
			return {
				...defaultData,
				...this.teamEditData[teamId],
			};
		},
		updateTeamAllocationSettings (teamId: string, { monthlyRunnitTokensBudget, isBoundToBudget }) {
			this.teamEditData = {
				...this.teamEditData,
				[teamId]: {
					...this.getTeamEditData(teamId),
					monthlyRunnitTokensBudget,
					isBoundToBudget,
				},
			};
		},
		updateTeamName (teamId: string, name: string) {
			this.teamEditData = {
				...this.teamEditData,
				[teamId]: {
					...this.getTeamEditData(teamId),
					name,
				},
			};
		},
		updateTeamCustomField(teamId: string, fieldKey: CompanyCustomFieldKey, value: any) {
			const currentData = this.getTeamEditData(teamId);
			this.teamEditData = {
				...this.teamEditData,
				[teamId]: {
					...currentData,
					customFields: {
						...(currentData.customFields || {}),
						[fieldKey]: value,
					},
				},
			};
		},
		async onSave () {
			if (!this.$refs.form.validate()) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Form invalid, check fields',
					show: true,
				});
				return;
			}

			this.saving = true;
			try {
				// Update team properties in parallel
				if (this.hasManageTeamsPermission) {
					const dbUpdates = this.currTeams
						.map((team: Team) => {
							const teamData = this.getTeamEditData(team.id);
							const trimmedTeamName: string = _trim(teamData.name);
							const customFields: Record<CompanyCustomFieldKey, any> = {};
							this.teamCustomFieldDefs.forEach((fieldDef: CompanyCustomFieldDef) => {
								switch (fieldDef.type) {
									case 'STR': {
										const val: string = _trim(teamData.customFields[fieldDef.key]);
										if (val) customFields[fieldDef.key] = val;
										break;
									}
									default:
										break;
								}
							});

							return db.collection('teams').doc(team.id).update({
								name: trimmedTeamName,
								customFields,
							});
						});

					if (dbUpdates.length > 0) {
						await Promise.all(dbUpdates);
					}
				}

				// Save all allocation settings in one call
				if (this.hasManageCompanyPermission) {
					try {
						await updateTeamTokenAllocationSettings(
							functions.httpsCallable('updateTeamTokenAllocationSettings'),
							this.currTeams.map((team: Team) => ({
								teamId: team.id,
								monthlyRunnitTokensBudget: this.getTeamEditData(team.id).monthlyRunnitTokensBudget,
								isBoundToBudget: this.getTeamEditData(team.id).isBoundToBudget,
							})),
						);
					} catch (e) {
						console.error('Error saving allocation settings:', e);
						// Don't block the flow if allocation settings fail
					}
				}

				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: `Success! ${pluralize(this.displayLabels.team, this.currTeams.length)} saved`,
					show: true,
				});
				this.$emit('success');
				this.onClose();
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error saving ${pluralize(this.displayLabels.team, this.currTeams.length)}, please reach out to report issues by going to Resources -> Help from the menu options`,
					show: true,
				});
			} finally {
				this.saving = false;
			}
		},
		onGlobalSettingsInput({ monthlyRunnitTokensBudget, isBoundToBudget }) {
			this.globalEditData = {
				monthlyRunnitTokensBudget,
				isBoundToBudget,
			};
			this.debouncedGlobalUpdate();
		},
		applyGlobalEditSettings () {
			this.teamEditData = this.sortedTeams.reduce((acc: Record<string, any>, t: Team) => ({
				...acc,
				[t.id]: {
					...this.getTeamEditData(t.id),
					monthlyRunnitTokensBudget: this.globalEditData.monthlyRunnitTokensBudget,
					isBoundToBudget: this.globalEditData.isBoundToBudget,
				},
			}), {});
		},
		clearGlobalEditSettings () {
			this.globalEditData = {
				monthlyRunnitTokensBudget: 0,
				isBoundToBudget: false,
			};
		},
		onGlobalEditChange (val: boolean) {
			this.isGlobalEdit = val;
			if (val) {
				this.clearGlobalEditSettings();
				this.applyGlobalEditSettings();
				this.expandedPanels = null;
			}
		},
		initTeamEditData (teams: Team[]) {
			if (!teams?.length) return;

			// Initialize team edit data
			this.teamEditData = teams.reduce((acc: Record<string, any>, team: Team) => ({
				...acc,
				[team.id]: {
					teamId: team.id,
					name: team.name,
					customFields: team.customFields || {},
					monthlyRunnitTokensBudget: team.allotmentSettings?.monthlyRunnitTokensBudget > 0
						? team.allotmentSettings.monthlyRunnitTokensBudget
						: 0,
					isBoundToBudget: !!team.allotmentSettings?.isBoundToBudget,
				},
			}), {});
		},
	},
	components: {
		IsBoundToBudgetBadge,
		DialogOrBottomSheet,
		DialogContent,
		ActionsIsland,
		SetTeamAllocationSettings,
	},
});
